import { action, makeObservable, observable } from "mobx";
import * as React from "react";
import { ButtonTypes } from "../../../../../components/ui/Button";
import { Icon, IconSymbols } from "../../../../../components/ui/Icon";
import { Panel } from "../../../../../components/ui/Panel";
import { IPaginationModel, PaginationModel } from "../../../../../components/widgets/pagination/Pagination_model";
import { FilterModel, FilterOperator, IFilterAttribute, IFilterModel } from "../../../../../core/filter/Filter_model";
import ProgressIndicatorModel, {
  ProgressIndicatorModel as IProgressIndicatorModel
} from "../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import I18n from "../../../../../core/localization/I18n";
import { ITableModel } from "../../../../../core/table/ITableModel";
import { TableModel } from "../../../../../core/table/Table_model";
import { UiActionRenderers } from "../../../../../core/uiAction/IUiAction";
import { Animations } from "../../../../../core/util/Animations";
import { BaseModel } from "../../../../../core/util/BaseModel";
import { Enums } from "../../../../../enums";
import ActionTypesApi, {
  ActionTypesApi as IActionTypesApi
} from "../../../../../services/api/v2/actionTypes/ActionTypes.api";
import { getActionTypeFormFields } from "../../../forms/actionType/ActionTypeFormFields_data";
import { SingleFormModel } from "../../../forms/singleFormModel/SingleForm_model";
import { GetActionTypeTableConfig } from "./ActionTypeSettings_config";
import { IFlightPathApiResponse } from "../../../../../services/api/v2/BaseApiModel";
import { IModalContextModel } from "../../../../../core/modalZ/context/IModalContext";
import ModalContext from "../../../../../core/modalZ/context/ModalContext";

export class ActionTypeSettingsModel extends BaseModel {
  modalService: IModalContextModel;
  actionTypeProvider: IActionTypesApi;
  httpProgress: IProgressIndicatorModel;
  orgId: number;
  paginationModel: IPaginationModel;
  filterModel: IFilterModel<FP.Entities.IActionType>;
  tableModel: ITableModel<FP.Entities.IActionType>;
  formModel: SingleFormModel;
  @observable searchInputValue: string = "";
  @observable.ref actionTypes: FP.Entities.IActionType[];

  constructor(organisationId: number) {
    super();
    makeObservable(this);
    this.modalService = ModalContext;
    this.actionTypeProvider = ActionTypesApi;
    this.httpProgress = ProgressIndicatorModel;
    this.orgId = organisationId;
    this.formModel = new SingleFormModel();
    this.installPagination();
    this.installFilter();
    this.initTable();
  }

  @action
  initTable = () => {
    this.tableModel = new TableModel();
    const tableConfig = GetActionTypeTableConfig(this);
    this.tableModel.set(tableConfig);
  };

  loadActionTypes = async () => {
    await this.filterModel.loadData();
  };

  setActionTypes = (actionTypes: FP.Entities.IActionType[]) => {
    this.actionTypes = actionTypes;
    this.tableModel.setData(actionTypes);
  };

  installPagination = () => {
    this.paginationModel = new PaginationModel();
    this.paginationModel.setConfig({ onPageClick: this.loadPageData });
  };

  installFilter = () => {
    const config = {
      paginationModel: this.paginationModel,
      initOpts: {
        filterCb: async filterOptions => await this.actionTypeProvider.getFilteredAsync(filterOptions, null, this.orgId)
      }
    };
    this.filterModel = new FilterModel(config);

    const organisationFilter: IFilterAttribute = {
      key: "organisationId",
      value: [this.orgId + ""],
      isHidden: true,
      operator: FilterOperator.EQUALS
    };

    const nameFilter: IFilterAttribute = {
      key: "name",
      value: [],
      operator: FilterOperator.CONTAINS
    };

    const lifeCycleFilter: IFilterAttribute = {
      key: "lifecycleStatus",
      value: [Enums.LifecycleStatus.Active + ""],
      isHidden: true,
      operator: FilterOperator.EQUALS
    };

    this.filterModel.addSort({
      key: "name",
      isAsc: true
    });

    this.filterModel.addFilter(organisationFilter);
    this.filterModel.addFilter(nameFilter);
    this.filterModel.addFilter(lifeCycleFilter);
    this.filterModel.setConfig({
      formFields: null,
      onDataLoaded: this.setActionTypes
    });
  };

  loadPageData = (index: number) => {
    this.filterModel.setConfig({ page: index });
    this.filterModel.loadData();
  };

  @action
  handleSearchInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    this.searchInputValue = e.currentTarget.value;
    this.filterModel.setFilterValue("name", e.currentTarget.value);
  };

  resetSearchInput = () => {
    this.filterModel.setFilterValue("name", "");
  };

  showFormModal = (actionType?: FP.Entities.IActionType) => {
    this.formModel.formFields = getActionTypeFormFields(this.orgId, actionType);
    this.formModel.actions = [
      {
        id: "cancel",
        label: I18n.t("phrases.cancel"),
        onAction: ev => {
          this.modalService.hide();
        },
        componentProps: {
          type: ButtonTypes.LINK,
          className: "ml-auto"
        },
        rendersIn: UiActionRenderers.BUTTON
      },
      {
        id: "CreateActionTypeButton",
        label: I18n.t("phrases.save"),
        rendersIn: UiActionRenderers.BUTTON,
        componentProps: {
          className: "ml-2"
        },
        onAction: async () => {
          const formRes = await this.formModel.submit();
          if (!formRes) return;

          let res: IFlightPathApiResponse<FP.Entities.IActionType>;

          this.modalService.hide();
          this.httpProgress.showOverlay();
          if (actionType) {
            res = await this.actionTypeProvider.update(this.orgId, actionType.id, formRes as FP.Entities.IActionType);
          } else {
            res = await this.actionTypeProvider.create(this.orgId, formRes as FP.Entities.IActionType);
          }
          this.httpProgress.hideOverlay();

          if (!res || res.isError) return;

          this.loadActionTypes();
        }
      }
    ];

    this.modalService.show({
      title: <h4 className="mt-6">{I18n.t("phrases.createActionType")}</h4>,
      showClose: true,
      content: <div className="container-fluid">{this.formModel.renderComponent()}</div>,
      componentProps: {
        wrapHeight: "full",
        wrapWidth: "small",
        position: "right",
        panelProps: {
          background: Panel.PanelBackgrounds.BG_LIGHT
        }
      },
      animationOptions: {
        animateIn: Animations.SLIDE_IN_RIGHT,
        animateOut: Animations.SLIDE_OUT_RIGHT,
        speed: 5
      }
    });
  };

  showConfirmDeleteModal = (actionType: FP.Entities.IActionType) => {
    return new Promise(resolve => {
      this.modalService.showConfirmDialog(
        <h1 className="mt-4">{I18n.t("phrases.confirm")}</h1>,
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <Icon symbol={IconSymbols.AlertCircle} className="mr-2" />
              {I18n.t("warnings.deleteActionType")}
            </div>
          </div>
          <div className="row">
            <div className="col">{I18n.t("phrases.confirmDelete", { name: actionType.name })}</div>
          </div>
        </div>,
        I18n.t("phrases.yes"),
        I18n.t("phrases.no"),
        {
          wrapWidth: "small",
          spacing: "small",
          position: "middle",
          panelProps: {
            background: Panel.PanelBackgrounds.BG_WHITE
          }
        },
        async () => {
          this.modalService.hide();

          this.httpProgress.showOverlay();
          const res = await this.actionTypeProvider.remove(this.orgId, actionType.id);
          this.httpProgress.hideOverlay();

          if (!res || res.isError) return;

          this.loadActionTypes();
        },
        () => {
          this.modalService.hide();
        },
        ButtonTypes.DANGER
      );
    });
  };
}
